/** Etiketten-Station (E11.5): Tablet im Laden, Gerätesitzung; Mieter melden sich mit Mieternummer + PIN an und drucken Etiketten am Ladendrucker. */
import { useEffect, useState } from "react";
import { Button, EmptyState, Notice, StatusBadge, TextField } from "../ui";
import { Marke } from "../ui/Marke";
import { api, ApiFehler, rufe } from "../api/client";
import { formatEuro } from "./format";

interface StationArtikel {
  id: string;
  code: string;
  bezeichnung: string;
  preisCent: number;
  etikettGedrucktAt: string | null;
  regalNummer: string;
}

export function Station({ ladenName }: { ladenName: string }) {
  const [nummer, setNummer] = useState("");
  const [pin, setPin] = useState("");
  const [sitzung, setSitzung] = useState<{ token: string; name: string } | null>(null);
  const [artikel, setArtikel] = useState<StationArtikel[] | null>(null);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<"62x29" | "a4">("62x29");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [rest, setRest] = useState(600);

  useEffect(() => {
    if (!sitzung) return;
    const t = setInterval(() => setRest((r) => (r <= 1 ? (abmelden(), 600) : r - 1)), 1000);
    return () => clearInterval(t);
  }, [sitzung]);

  function abmelden() {
    setSitzung(null);
    setArtikel(null);
    setAuswahl(new Set());
    setNummer("");
    setPin("");
    setRest(600);
  }

  async function anmelden() {
    setFehler(null);
    setLaeuft(true);
    try {
      const r = await rufe<{ token: string; name: string }>(api.api.kasse.station.anmelden.$post({ json: { mieternummer: nummer, pin } }));
      const a = await rufe<{ artikel: StationArtikel[] }>(api.api.kasse.station[":token"].artikel.$get({ param: { token: r.token } }));
      setSitzung(r);
      setArtikel(a.artikel);
      setAuswahl(new Set(a.artikel.filter((x) => !x.etikettGedrucktAt).map((x) => x.id)));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : "Anmeldung hat nicht geklappt.");
    } finally {
      setLaeuft(false);
    }
  }

  if (!sitzung) {
    return (
      <div className="kasse-center">
        <Marke size={56} />
        <h1 className="display">Etiketten drucken</h1>
        <p className="sc-muted">{ladenName} · Mieternummer und deine Station-PIN eingeben. Die PIN legst du im Mieterportal unter Sicherheit fest.</p>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <div className="sc-col" style={{ gap: "var(--space-3)", width: "min(360px, 100%)" }}>
          <TextField label="Mieternummer" value={nummer} onChange={(e) => setNummer(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" autoComplete="off" autoFocus />
          <TextField label="PIN" type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="off" onKeyDown={(e) => e.key === "Enter" && void anmelden()} />
          <Button variant="primary" size="lg" block disabled={nummer.length !== 4 || pin.length < 4} loading={laeuft} onClick={() => void anmelden()}>Anmelden</Button>
        </div>
      </div>
    );
  }

  const liste = artikel ?? [];
  const ids = [...auswahl].filter((id) => liste.some((a) => a.id === id));
  return (
    <div className="kasse-center" style={{ alignItems: "stretch" }}>
      <div className="sc-spread">
        <h1 className="title">Hallo {sitzung.name} – Etiketten</h1>
        <span className="sc-toolbar"><StatusBadge status="neutral">Abmeldung in {Math.floor(rest / 60)}:{String(rest % 60).padStart(2, "0")}</StatusBadge><Button variant="quiet" icon="log-out" onClick={abmelden}>Fertig</Button></span>
      </div>
      {liste.length === 0 ? <EmptyState illustration="empty-shelf" title="Keine Artikel im Regal">Erst im Portal erfassen, dann hier drucken.</EmptyState> : (
        <>
          <div className="sc-toolbar">
            <Button variant="quiet" onClick={() => setAuswahl(new Set(liste.filter((a) => !a.etikettGedrucktAt).map((a) => a.id)))}>Nur ohne Etikett</Button>
            <Button variant="quiet" onClick={() => setAuswahl(new Set(liste.map((a) => a.id)))}>Alle</Button>
            <Button variant="quiet" onClick={() => setAuswahl(new Set())}>Keine</Button>
            <span className="sc-grow" />
            <select className="sc-select" aria-label="Format" value={format} onChange={(e) => setFormat(e.target.value as "62x29" | "a4")}><option value="62x29">Etikettendrucker 62 × 29</option><option value="a4">A4-Bogen</option></select>
          </div>
          <div className="app-tabellen-scroll" style={{ maxHeight: "50vh", overflow: "auto" }}>
            <table className="rg-table">
              <thead><tr><th /><th>Code</th><th>Artikel</th><th>Regal</th><th className="is-num">Preis</th><th>Etikett</th></tr></thead>
              <tbody>
                {liste.map((a) => (
                  <tr key={a.id}>
                    <td><input type="checkbox" aria-label={`${a.code} drucken`} checked={auswahl.has(a.id)} onChange={(e) => { const n = new Set(auswahl); if (e.target.checked) n.add(a.id); else n.delete(a.id); setAuswahl(n); }} /></td>
                    <td className="app-code">{a.code}</td>
                    <td>{a.bezeichnung}</td>
                    <td className="sc-num">{a.regalNummer}</td>
                    <td className="is-num">{formatEuro(a.preisCent)}</td>
                    <td>{a.etikettGedrucktAt ? <StatusBadge status="paid">gedruckt</StatusBadge> : <StatusBadge status="attention">fehlt</StatusBadge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="primary" size="lg" block icon="printer" disabled={!ids.length} onClick={() => { window.open(`/api/kasse/station/${sitzung.token}/etiketten.pdf?ids=${ids.join(",")}&format=${format}`, "_blank", "noopener"); setArtikel(liste.map((a) => (ids.includes(a.id) ? { ...a, etikettGedrucktAt: new Date().toISOString() } : a))); }}>{ids.length} Etiketten drucken</Button>
        </>
      )}
    </div>
  );
}
